import React, { useRef, useEffect, useCallback } from 'react';
import Canvas from './Canvas';
import { ShapeManager } from './ShapeManager';

// デバイス種類の設定
const DEVICE_SETTINGS = {
  PC: {
    maxShapes: 25,
    frameRate: 60,
    shapeSpeed: 1.0,
    bassThreshold: 70,
    maxDiffThreshold: 40,
    randomThreshold: 0.9,
    initialDelay: 0,
    trailLength: 12,
    trailInterval: 2,
    trailAlphaFactor: 0.5
  },
  MOBILE: {
    maxShapes: 13,
    frameRate: 30,
    shapeSpeed: 0.5,
    bassThreshold: 50,
    maxDiffThreshold: 30,
    randomThreshold: 0.95,
    initialDelay: 100,
    trailLength: 6,
    trailInterval: 3,
    trailAlphaFactor: 0.3
  }
};

// モバイルデバイスの判定
const isMobile = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const AudioVisualizer = ({ analyser }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const shapeManagerRef = useRef(null);
  const deviceSettings = useRef(DEVICE_SETTINGS.PC);

  useEffect(() => {
    const canvasElement = canvasRef.current?.canvas;
    if (!canvasElement || !analyser) return;

    // デバイス設定を更新
    deviceSettings.current = isMobile() ? DEVICE_SETTINGS.MOBILE : DEVICE_SETTINGS.PC;

    shapeManagerRef.current = new ShapeManager(
      deviceSettings.current.maxShapes,
      deviceSettings.current.frameRate
    );

    return () => {
      shapeManagerRef.current.clearShapes();
    };
  }, [analyser]);

  const analyzeAudio = useCallback((analyser) => {
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    
    // ベースの平均値を計算
    const bassAverage = dataArray.slice(0, 10).reduce((acc, val) => acc + val, 0) / 10;
    
    // 最大値と最小値の差を計算
    const maxDiff = Math.max(...dataArray) - Math.min(...dataArray);
    
    return { bassAverage, maxDiff };
  }, []);

  const animate = useCallback((timestamp) => {
    if (timestamp - lastFrameTimeRef.current < 1000 / deviceSettings.current.frameRate) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }
    lastFrameTimeRef.current = timestamp;

    const { bassAverage, maxDiff } = analyzeAudio(analyser);

    // 形状の生成条件をチェック
    if (shapeManagerRef.current.shouldCreateShape(bassAverage, maxDiff)) {
      shapeManagerRef.current.createShape(
        bassAverage + maxDiff,
        canvasRef.current.canvas.width,
        canvasRef.current.canvas.height
      );
    }

    // 形状の更新と描画
    shapeManagerRef.current.updateShapes();
    
    // Canvasコンポーネントに形状を描画させる
    const ctx = canvasRef.current.canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current.canvas.width, canvasRef.current.canvas.height);
      shapeManagerRef.current.drawShapes(ctx);
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [analyser, analyzeAudio]);

  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  return (
    <Canvas
      ref={canvasRef}
      width={window.innerWidth}
      height={window.innerHeight}
      onResize={() => {
        if (canvasRef.current) {
          canvasRef.current.resize();
        }
      }}
    />
  );
};

export default AudioVisualizer;